import { FC } from "react";

import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";

import { muiTheme } from "@fllite/ui/themes";
import { MyTripsBusinessCase } from "@fllite/ui/types";

import { BusinessCaseRow } from "./BusinessCaseRow";
import { BusinessCaseRowMobile } from "./BusinessCaseRowMobile";
import {
  Card,
  EmptyState,
  HeadCell,
  SectionTitle,
  TableScroll,
} from "./styles";

type MetaDateAccessor = (
  businessCase: MyTripsBusinessCase,
) => string | null | undefined;

type ActionNeededAccessor = (businessCase: MyTripsBusinessCase) => boolean;

interface BusinessCaseColumnProps {
  title: string;
  metaLabel: string;
  metaDate: MetaDateAccessor;
  data: MyTripsBusinessCase[];
  isLoading: boolean;
  emptyMessage: string;
  showActionNeededBadge?: ActionNeededAccessor;
}

// Itinerary / Customer / Tail / <metaLabel> / Status / chevron.
const DESKTOP_COLUMN_COUNT = 6;

const SKELETON_ROWS = [0, 1, 2];

export const BusinessCaseColumn: FC<BusinessCaseColumnProps> = ({
  title,
  metaLabel,
  metaDate,
  data,
  isLoading,
  emptyMessage,
  showActionNeededBadge,
}) => {
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up("md"));
  const columnCount = isDesktop ? DESKTOP_COLUMN_COUNT : 1;

  const body = isLoading ? (
    <TableBody>
      {SKELETON_ROWS.map((row) => (
        <TableRow key={row}>
          <TableCell colSpan={columnCount}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  ) : data.length === 0 ? (
    <TableBody>
      <TableRow>
        <TableCell colSpan={columnCount} sx={{ p: 0, border: 0 }}>
          <EmptyState>{emptyMessage}</EmptyState>
        </TableCell>
      </TableRow>
    </TableBody>
  ) : (
    <TableBody>
      {data.map((businessCase) =>
        isDesktop ? (
          <BusinessCaseRow
            key={businessCase.id}
            businessCase={businessCase}
            metaDate={metaDate(businessCase)}
            actionNeeded={
              showActionNeededBadge ? showActionNeededBadge(businessCase) : false
            }
          />
        ) : (
          <BusinessCaseRowMobile
            key={businessCase.id}
            businessCase={businessCase}
            metaDate={metaDate(businessCase)}
            metaLabel={metaLabel}
            actionNeeded={
              showActionNeededBadge ? showActionNeededBadge(businessCase) : false
            }
          />
        ),
      )}
    </TableBody>
  );

  return (
    <Card>
      <SectionTitle>{title}</SectionTitle>
      <TableScroll>
        <Table>
          {isDesktop && (
            <TableHead>
              <TableRow>
                <HeadCell>Itinerary</HeadCell>
                <HeadCell>Customer</HeadCell>
                <HeadCell>Tail</HeadCell>
                <HeadCell>{metaLabel}</HeadCell>
                <HeadCell align="right">Status</HeadCell>
                <HeadCell />
              </TableRow>
            </TableHead>
          )}
          {body}
        </Table>
      </TableScroll>
    </Card>
  );
};
